import { DateTime } from "luxon";
import { amountFormatter } from "./formatAmount";

export const prettifyJson = (jsonObject: object) => {
  if (typeof jsonObject !== "object") return;
  return JSON.stringify(jsonObject, null, 2);
};

type DateDisplayVariant = "long" | "short";

interface DateTimeFormatterTypes {
  date: string;
  variant?: DateDisplayVariant;
  timeZone?: string;
  mode?: "date" | "time" | "datetime";
  showIST?: boolean;
}

export const dateTimeFormatter = ({
  date,
  variant = "long",
  timeZone = "Asia/Kolkata",
  mode = "datetime",
  showIST = true,
}: DateTimeFormatterTypes) => {
  if (!date) return "N/A";

  const dt = DateTime.fromISO(date).setZone(timeZone);
  if (!dt.isValid) return "N/A";

  const hasTime = date.includes("T") || date.includes(":");

  const formatMap = {
    long: {
      date: "dd MMMM yyyy", // 02 March 2026
      time: "hh:mm a",
    },
    short: {
      date: "dd MMM yyyy", // 02 Mar 2026
      time: "hh:mm a",
    },
  };

  const selectedFormat = formatMap[variant];

  let result = "";

  if (mode === "date" || mode === "datetime") {
    result += dt.toFormat(selectedFormat.date);
  }

  if (mode === "datetime" && hasTime) {
    if (result) result += " - ";
    result += dt.toFormat(selectedFormat.time);

    if (showIST && timeZone === "Asia/Kolkata") {
      result += " (IST)";
    }
  }

  if (mode === "time") {
    if (!hasTime) return "N/A";
    result = dt.toFormat(selectedFormat.time);
    if (showIST && timeZone === "Asia/Kolkata") {
      result += " (IST)";
    }
  }

  return result || "N/A";
};

export const addRupeeCharToFormattedAmount = (amount: any) => {
  return `₹ ${amountFormatter(amount)}`;
};

export function buildUrl(base: string, path: string = ""): string {
  if (!base) {
    throw new Error("Base URL is required");
  }

  try {
    // base should already be origin (no trailing slash)
    const baseUrl = new URL(base);

    // Append path safely
    const fullPath = path
      .replace(/^\/+/, "/") // remove leading //
      .replace(/\/+$/, ""); // remove trailing /

    const final = new URL(fullPath || "/", baseUrl).toString();

    // Clean any accidental double slashes (rare)
    return final.replace(/([^:]\/)\/+/g, "$1");
  } catch (err) {
    console.error("buildUrl failed:", { base, path, error: err });
    throw new Error(`Cannot build URL from base="${base}" and path="${path}"`);
  }
}

// Helper function to mask seller name
export const maskSellerName = (name: string | null | undefined): string => {
  if (!name || name === "NA" || name === "N/A") return "NA";
  const trimmedName = name.trim();
  if (trimmedName.length <= 2) return trimmedName;
  return trimmedName.substring(0, 2) + "***";
};

export const capitalizeText = (value?: string) => {
  if (!value) return "";
  return value
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
